import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Bell, BookOpen, Users, Trophy, Dumbbell, Calendar, Settings, Inbox, Clock } from "lucide-react";
import { api } from "@/lib/api";

const ICONS = { book: BookOpen, users: Users, trophy: Trophy, dumbbell: Dumbbell, calendar: Calendar };

function Toggle({ on, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      role="switch"
      aria-checked={on}
      className={`relative w-11 h-6 rounded-full transition-colors duration-200 shrink-0 ${on ? "bg-brand" : "bg-zinc-300"}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${on ? "translate-x-5" : ""}`} />
    </button>
  );
}

export default function NotificationsModal({ open, onClose }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("recent");
  const [feed, setFeed] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [cats, setCats] = useState([]);
  const [meta, setMeta] = useState({});

  const load = useCallback(() => {
    api.get("/notifications/feed").then((r) => setFeed(r.data.items || [])).catch(() => setFeed([]));
    api.get("/notifications/prefs").then((r) => {
      setPrefs(r.data.prefs); setCats(r.data.categories || []);
      setMeta({ quiet: r.data.quiet_hours, cap: r.data.daily_cap });
    }).catch(() => {});
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try {
      await api.put("/notifications/prefs", { prefs: next });
      const r = await api.get("/notifications/feed");
      setFeed(r.data.items || []);
    } catch {}
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid="notifications-modal"
          className="fixed inset-0 z-[2000] bg-black/40 backdrop-blur-sm flex items-start justify-center sm:justify-end p-4 pt-20"
        >
          <motion.div
            initial={{ opacity: 0, y: -12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -12, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 320, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm bg-white rounded-2xl border border-border shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2 font-semibold text-sm">
                <Bell size={16} className="text-brand" /> Notifications
              </div>
              <button data-testid="notifications-close" onClick={onClose} className="p-1.5 rounded-md text-muted-foreground hover:bg-secondary">
                <X size={16} />
              </button>
            </div>

            <div className="flex px-2 pt-2 gap-1">
              {[["recent", "Recent", Inbox], ["settings", "Settings", Settings]].map(([k, label, Icon]) => (
                <button
                  key={k}
                  data-testid={`notifications-tab-${k}`}
                  onClick={() => setTab(k)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-colors ${tab === k ? "bg-brand text-white" : "text-muted-foreground hover:bg-secondary"}`}
                >
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>

            <div className="p-3 max-h-[60vh] overflow-y-auto">
              {tab === "recent" && (
                <div className="space-y-2" data-testid="notifications-feed">
                  {feed === null ? (
                    <div className="text-sm text-muted-foreground p-3">Loading…</div>
                  ) : feed.length === 0 ? (
                    <div className="text-sm text-muted-foreground p-4 text-center">
                      <Bell size={22} className="mx-auto mb-2 text-zinc-300" />
                      Nothing new right now. We'll ping you when your crew's up to something.
                    </div>
                  ) : feed.map((n) => {
                    const Icon = ICONS[n.icon] || Bell;
                    return (
                      <button
                        key={n.id}
                        data-testid={`notification-${n.category}`}
                        onClick={() => { navigate(n.cta); onClose(); }}
                        className="w-full text-left flex items-start gap-3 rounded-xl border border-border p-3 hover:border-brand hover:bg-brand/[0.03] transition-colors"
                      >
                        <span className="w-8 h-8 rounded-lg bg-brand/10 text-brand flex items-center justify-center shrink-0">
                          <Icon size={16} />
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold">{n.title}</div>
                          <div className="text-xs text-muted-foreground">{n.body}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {tab === "settings" && prefs && (
                <div data-testid="notifications-settings">
                  <div className="flex items-center justify-between p-3 rounded-xl bg-secondary/60 mb-3">
                    <div>
                      <div className="text-sm font-semibold">All notifications</div>
                      <div className="text-xs text-muted-foreground">Master switch for friend nudges</div>
                    </div>
                    <Toggle on={!!prefs.master} onClick={() => toggle("master")} testid="notif-toggle-master" />
                  </div>
                  <div className={`space-y-1 ${prefs.master ? "" : "opacity-40 pointer-events-none"}`}>
                    {cats.map((c) => (
                      <div key={c.key} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold">{c.label}</div>
                          <div className="text-xs text-muted-foreground">{c.hint}</div>
                        </div>
                        <Toggle on={!!prefs[c.key]} onClick={() => toggle(c.key)} testid={`notif-toggle-${c.key}`} />
                      </div>
                    ))}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-3 flex items-start gap-1.5 px-1">
                    <Clock size={13} className="mt-0.5 shrink-0" />
                    <span>Quiet hours {meta.quiet?.start}:00–{meta.quiet?.end}:00, never during class, max {meta.cap}/day. We keep it light.</span>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
